import * as React from 'react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
  Snackbar,
  Alert,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { FiDownload, FiTrash2, FiUpload } from 'react-icons/fi';
import GridMui from '../../components/gridMui';
import api from '../../services/api';
import Loading from '../../components/loading';
import AlertDialogSlide from '../../components/alertDialog';
import {
  Container,
  Flex,
  Card,
  CardHeader,
  CardContent,
} from '../../components/ui';
import { useTheme } from '../../context/ThemeContext';

interface DashboardContextFile {
  fileId: number;
  originalname: string;
  storedFilename: string;
  usergroupId: number;
  usergroupName: string;
  uploadedByEmail: string;
  uploadedByName: string;
  createdAt: string;
}

interface UserGroupOption {
  userGroupId: number;
  name: string;
}

interface AlertState {
  open: boolean;
  message: string;
  severity: 'success' | 'info' | 'warning' | 'error';
}

const DashboardContextFilesPage: React.FC = () => {
  const { isDarkMode } = useTheme();
  const [files, setFiles] = useState<DashboardContextFile[]>([]);
  const [showNoData, setShowNoData] = useState(false);

  const [usergroupOptions, setUsergroupOptions] = useState<UserGroupOption[]>(
    [],
  );

  const [selectedUsergroupId, setSelectedUsergroupId] = useState<number | ''>(
    '',
  );
  const [selectedFile, setSelectedFile] = useState<File | undefined>();
  const [isUploading, setIsUploading] = useState(false);
  const [alertState, setAlertState] = useState<AlertState>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [confirmDelete, setConfirmDelete] =
    useState<DashboardContextFile | null>(null);

  const muiTheme = createTheme({
    palette: { mode: isDarkMode ? 'dark' : 'light' },
    components: {
      // @ts-expect-error - MuiDataGrid override
      MuiDataGrid: {
        styleOverrides: {
          root: {
            backgroundColor: isDarkMode ? '#1C1C1E' : '#FFFFFF',
            color: isDarkMode ? '#FFFFFF' : '#263238',
            border: `1px solid ${isDarkMode ? '#48484A' : '#E5E5EA'}`,
            '& .MuiDataGrid-cell': {
              color: isDarkMode ? '#FFFFFF' : '#263238',
              borderColor: isDarkMode ? '#48484A' : '#E5E5EA',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: isDarkMode ? '#2C2C2E' : '#F5F5F5',
              color: isDarkMode ? '#FFFFFF' : '#263238',
              borderColor: isDarkMode ? '#48484A' : '#E5E5EA',
            },
            '& .MuiDataGrid-columnHeader': {
              color: isDarkMode ? '#FFFFFF' : '#263238',
            },
            '& .MuiDataGrid-row': {
              backgroundColor: isDarkMode ? '#1C1C1E' : '#FFFFFF',
              '&:nth-of-type(even)': {
                backgroundColor: isDarkMode ? '#2C2C2E' : '#FAFAFA',
              },
              '&:hover': {
                backgroundColor: isDarkMode ? '#3A3A3C' : '#F0F0F0',
              },
            },
            '& .MuiDataGrid-footerContainer': {
              backgroundColor: isDarkMode ? '#2C2C2E' : '#F5F5F5',
              color: isDarkMode ? '#FFFFFF' : '#263238',
              borderColor: isDarkMode ? '#48484A' : '#E5E5EA',
            },
          },
        },
      },
    },
  });

  const showAlert = (
    message: string,
    severity: AlertState['severity'],
  ): void => {
    setAlertState({ open: true, message, severity });
  };

  const closeAlert = (
    _e?: React.SyntheticEvent | Event,
    reason?: string,
  ): void => {
    if (reason === 'clickaway') return;
    setAlertState(prev => ({ ...prev, open: false }));
  };

  const loadFiles = useCallback(() => {
    setShowNoData(false);
    api
      .get('/dashboardContextFiles')
      .then(response => {
        setFiles(response.data ?? []);
        setShowNoData(true);
      })
      .catch(error => {
        console.error(error?.response?.data?.message ?? error);
        setShowNoData(true);
      });
  }, []);

  const loadOptions = useCallback(() => {
    api
      .get('/usergroups/getAll')
      .then(response => {
        const groups = (response.data?.usergroups ?? []).map((g: any) => ({
          userGroupId: g.userGroupId,
          name: g.name,
        }));
        setUsergroupOptions(groups);
      })
      .catch(error => {
        console.error(error?.response?.data?.message ?? error);
      });
  }, []);

  useEffect(() => {
    loadFiles();
    loadOptions();
  }, [loadFiles, loadOptions]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.md')) {
      showAlert('Only .md files are allowed', 'error');
      e.target.value = '';
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      showAlert('The file exceeds the maximum allowed size of 10MB.', 'error');
      e.target.value = '';
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = (): void => {
    if (!selectedUsergroupId || !selectedFile) {
      showAlert('Please select a user group and a .md file', 'error');
      return;
    }

    const usergroup = usergroupOptions.find(
      g => g.userGroupId === selectedUsergroupId,
    );
    if (!usergroup) {
      showAlert('Invalid user group', 'error');
      return;
    }

    const data = new FormData();
    data.append('file', selectedFile);
    data.append('usergroupId', String(usergroup.userGroupId));
    data.append('usergroupName', usergroup.name);

    setIsUploading(true);
    api
      .post('/dashboardContextFiles', data, {
        headers: { 'content-type': 'multipart/form-data' },
      })
      .then(() => {
        showAlert('File uploaded successfully', 'success');
        setSelectedFile(undefined);
        setSelectedUsergroupId('');
        const input = document.getElementById(
          'dashboard-context-file-input',
        ) as HTMLInputElement | null;
        if (input) input.value = '';
        loadFiles();
      })
      .catch(error => {
        const msg =
          error?.response?.data?.message ??
          error?.message ??
          'Error uploading file';
        showAlert(msg, 'error');
      })
      .finally(() => {
        setIsUploading(false);
      });
  };

  const handleDownload = useCallback((file: DashboardContextFile): void => {
    api
      .get(`/dashboardContextFiles/${file.fileId}/download`, {
        responseType: 'blob',
      })
      .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', file.storedFilename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(error => {
        const msg = error?.response?.data?.message ?? 'Error downloading file';
        showAlert(msg, 'error');
      });
  }, []);

  const handleDelete = useCallback((file: DashboardContextFile): void => {
    api
      .delete(`/dashboardContextFiles/${file.fileId}`)
      .then(() => {
        showAlert('File deleted', 'success');
        setFiles(prev => prev.filter(f => f.fileId !== file.fileId));
      })
      .catch(error => {
        const msg = error?.response?.data?.message ?? 'Error deleting file';
        showAlert(msg, 'error');
      });
  }, []);

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'usergroupName',
        headerName: 'UserGroup',
        flex: 1,
      },
      {
        field: 'uploadedByEmail',
        headerName: 'Email',
        flex: 1,
      },
      {
        field: 'createdAt',
        headerName: 'Uploaded At',
        flex: 0.9,
        valueGetter: (_v, row: DashboardContextFile) => {
          if (!row.createdAt) return '';
          const d = new Date(row.createdAt);
          const pad = (n: number): string => n.toString().padStart(2, '0');
          return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: params => (
          <>
            <IconButton
              size="small"
              onClick={() => handleDownload(params.row as DashboardContextFile)}
              aria-label="download"
            >
              <FiDownload />
            </IconButton>
            <IconButton
              size="small"
              onClick={() =>
                setConfirmDelete(params.row as DashboardContextFile)
              }
              aria-label="delete"
            >
              <FiTrash2 />
            </IconButton>
          </>
        ),
      },
    ],
    [handleDownload],
  );

  const rows = files.map(f => ({ id: f.fileId, ...f }));

  return (
    <Container maxWidth="full" padding>
      <Card variant="elevated" padding="large" fullHeight>
        <CardHeader>
          <h1 style={{ margin: 0 }}>Dashboard Context</h1>
        </CardHeader>
        <CardContent>
          <Flex
            direction="row"
            wrap="wrap"
            gap={2}
            align="center"
            style={{ marginBottom: 24 }}
          >
            <TextField
              select
              label="User Group"
              size="small"
              value={selectedUsergroupId}
              onChange={e =>
                setSelectedUsergroupId(
                  e.target.value ? Number(e.target.value) : '',
                )
              }
              sx={{
                minWidth: 220,
                '& .MuiInputBase-root': {
                  backgroundColor: '#FFFFFF',
                  color: '#263238',
                },
                '& .MuiInputLabel-root': { color: '#263238' },
                '& .MuiInputLabel-root.MuiInputLabel-shrink': {
                  color: 'primary.main',
                  backgroundColor: '#FFFFFF',
                  padding: '0 15px',
                },
                '& .MuiSvgIcon-root': { color: '#263238' },
              }}
            >
              {usergroupOptions.map(g => (
                <MenuItem key={g.userGroupId} value={g.userGroupId}>
                  {g.name}
                </MenuItem>
              ))}
            </TextField>

            <Button
              variant="contained"
              color="primary"
              component="label"
              startIcon={<FiUpload />}
            >
              {selectedFile ? selectedFile.name : 'Select .md file'}
              <input
                id="dashboard-context-file-input"
                hidden
                type="file"
                accept=".md,text/markdown"
                onChange={handleFileChange}
              />
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={handleUpload}
              disabled={isUploading || !selectedFile || !selectedUsergroupId}
              sx={{
                '&.Mui-disabled': {
                  backgroundColor: 'primary.main',
                  color: '#FFFFFF',
                  opacity: 0.55,
                },
              }}
            >
              {isUploading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Upload'
              )}
            </Button>

            <Button
              variant="contained"
              color="primary"
              component="a"
              href="/dashboard_context_template.md"
              download="dashboard_context_template.md"
              startIcon={<FiDownload />}
              sx={{ marginLeft: 'auto' }}
            >
              Download template
            </Button>
          </Flex>

          <ThemeProvider theme={muiTheme}>
            {files.length > 0 ? (
              <GridMui columns={columns} rows={rows} pageSizeOptions={20} />
            ) : showNoData ? (
              <Flex
                justify="center"
                align="center"
                style={{ minHeight: '300px' }}
              >
                <span>No dashboard context file uploaded yet</span>
              </Flex>
            ) : (
              <Flex
                justify="center"
                align="center"
                style={{ minHeight: '300px' }}
              >
                <Loading />
              </Flex>
            )}
          </ThemeProvider>
        </CardContent>
      </Card>

      {confirmDelete && (
        <ThemeProvider theme={muiTheme}>
          <AlertDialogSlide
            title="Delete file"
            msg="Are you sure you want to delete the file?"
            handleYes={() => {
              handleDelete(confirmDelete);
              setConfirmDelete(null);
            }}
            setShow={(show: boolean) => {
              if (!show) setConfirmDelete(null);
            }}
          />
        </ThemeProvider>
      )}

      <Snackbar
        open={alertState.open}
        autoHideDuration={5000}
        onClose={closeAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={closeAlert}
          severity={alertState.severity}
          sx={{ width: '100%' }}
        >
          {alertState.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DashboardContextFilesPage;
